import React from "react"
import { useMediaQuery } from "@material-ui/core"
import { useAppContext } from "../../../app-context/use-app-context"
import { useUrlWithContext } from "../../../hooks/use-url-with-context"
import { useTranslations, type Translations } from "./use-translations"
import { type NavItemResponse, useGetNavItemsQuery } from "./use-nav-items-query"
import { paths } from "../../../paths"
import { HELP_URL } from "../../../constants"
import {
  Home,
  Folder,
  Clipboard,
  Columns,
  Calendar,
  CheckCircle,
  Settings,
  Mail,
  CreditCard,
  Grid,
  Users,
  Clock,
  HelpCircle,
} from "react-feather"

const ICON_MAP: Record<string, React.ComponentType> = {
  Home,
  Folder,
  Clipboard,
  CheckCircle,
  Calendar,
  Columns,
  Grid,
  Users,
  Mail,
  Settings,
  CreditCard,
  Clock,
}

export const containsHourReportsItem = (items: NavItemResponse[]): boolean => {
  return items.some((item) => {
    if (item.id === "hour-reports") return true
    if ("items" in item) return containsHourReportsItem(item.items)

    return item.path.split("?")[0].replace(/\/$/, "") === paths.hourReports()
  })
}

const toNavItem = (
  itemConfig: NavItemResponse,
  translations: Translations,
  createPathWithContext: (path: string) => string
): NavItem => {
  const Icon = itemConfig.icon ? ICON_MAP[itemConfig.icon as unknown as keyof typeof ICON_MAP] : null
  const label = translations[itemConfig.labelKey as keyof Translations]

  if ("items" in itemConfig) {
    return {
      id: itemConfig.id,
      label,
      items: itemConfig.items.map((subItem) => toNavItem(subItem, translations, createPathWithContext)),
      icon: Icon ? React.createElement(Icon) : null,
    }
  }

  return {
    id: itemConfig.id,
    label,
    icon: Icon ? React.createElement(Icon) : null,
    path: createPathWithContext(itemConfig.path),
  }
}

export const useNavItems = () => {
  const { appContext } = useAppContext()
  const { mainContext, subContext } = appContext
  const { createPathWithContext } = useUrlWithContext()
  const isMobileScreen = useMediaQuery(() => "(max-width:500px)")
  const translations = useTranslations()
  const contextType = subContext?.type || mainContext?.type
  const contextId = subContext?.id || mainContext?.id
  const { data: navItemsConfig, isSuccess } = useGetNavItemsQuery(
    {
      contextType: contextType as string,
      contextId: contextId as string,
      isMobileScreen,
    },
    {
      skip: !contextType || !contextId,
    }
  )

  const navItems = React.useMemo(() => {
    const contextNavItems = navItemsConfig
      ? navItemsConfig.map((itemConfig) => toNavItem(itemConfig, translations, createPathWithContext))
      : []

    return [
      ...contextNavItems,
      {
        id: "help",
        label: translations.helpLabel,
        path: HELP_URL,
        icon: <HelpCircle />,
        external: true as const,
      },
    ]
  }, [navItemsConfig, translations, createPathWithContext])

  return {
    navItems,
    isNavItemsSuccess: isSuccess,
    hasHourReportsNavItem: navItemsConfig ? containsHourReportsItem(navItemsConfig) : false,
  }
}

export type NavItem =
  | {
      id: string
      label: string
      path: string
      icon?: React.ReactNode
      external?: boolean
    }
  | {
      id: string
      label: string
      items: NavItem[]
      icon?: React.ReactNode
    }
